import { isNowInSchedule } from "@/lib/schedule"
import { coversTime, normalizeHolidayHours, tenantClock } from "@/lib/scheduling/normalize-hours"
import type { ApiData, Service, ServiceHours, ServiceType } from "@/types"

/**
 * Whether a holiday window on the service covers the given instant.
 *
 * Holiday windows are CLOSED windows: while they hold, the service does not
 * accept orders regardless of week hours. Both the pause and the clock are
 * read on the TENANT's zone through `normalizeHolidayHours`, so a UTC
 * time-of-day payload lands on the calendar day it really covers (§9.1).
 *
 * The all-day pause the portal writes is 00:00:00-23:59:59; cut to 23:59 it
 * still covers the final minute, so the whole day stays paused.
 */
export function isServiceOnHoliday(
  service: Service | null | undefined,
  now: Date = new Date(),
  timeZone?: string | null,
): boolean {
  const holidayHours = service?.serviceHours?.holidayHours
  if (!holidayHours?.length) return false

  const clock = tenantClock(now, timeZone)

  return normalizeHolidayHours(holidayHours, timeZone).some(
    (window) => window.date === clock.date && coversTime(window, clock.time),
  )
}

/**
 * Whether the given service channel is accepting orders right now.
 *
 * Does NOT include the merchant-level open-store toggle (`ApiData.status`) —
 * pair with `isMerchantStoreToggledOff` / `isServiceOpenInCatalog` for that.
 *
 * Closed when the service is missing, not AVAILABLE, on a holiday window, or
 * outside its weekly hours (empty schedule = closed).
 */
export function isServiceOpen(
  service: Service | null | undefined,
  now: Date = new Date(),
  timeZone?: string | null,
): boolean {
  if (!service) return false
  if (service.status !== "AVAILABLE") return false
  if (isServiceOnHoliday(service, now, timeZone)) return false
  return isNowInSchedule(service.serviceHours?.weekHours, now, timeZone)
}

/** Merchant-level "open store" toggle from the portal. Closes every channel. */
export function isMerchantStoreToggledOff(data: Pick<ApiData, "status">): boolean {
  return data.status === "UNAVAILABLE"
}

export function findServiceByType(
  data: Pick<ApiData, "services">,
  serviceType: ServiceType,
): Service | undefined {
  return data.services.find((s) => s.serviceType === serviceType)
}

/**
 * Whether the given channel is open in a catalog snapshot — merchant toggle
 * AND the matching service's own hours/status.
 */
export function isServiceOpenInCatalog(
  data: ApiData,
  serviceType: ServiceType,
  now: Date = new Date(),
  timeZone?: string | null,
): boolean {
  if (isMerchantStoreToggledOff(data)) return false
  return isServiceOpen(findServiceByType(data, serviceType), now, timeZone)
}

/**
 * Human-readable channel label for closed-store error messages (PT-BR).
 */
export function serviceTypeLabel(serviceType: ServiceType): string {
  switch (serviceType) {
    case "DELIVERY":
      return "delivery"
    case "TAKEOUT":
      return "retirada"
    case "INDOOR":
      return "consumo no local"
    default:
      return "este canal"
  }
}

/** Week hours of a service, for next-opening messages. */
export function serviceWeekHours(
  service: Service | null | undefined,
): ServiceHours["weekHours"] | null {
  return service?.serviceHours?.weekHours ?? null
}
